import React, { useState } from 'react';
import { Search, Bell, CircleHelp, Moon } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext.tsx';
import { View } from '../../../types.ts';
import { useBranding } from '../../../hooks/useBranding.ts';

interface AdminTopbarProps {
  isSidebarCollapsed: boolean;
  toggleSidebar: () => void;
  setCurrentView: (view: View) => void;
}

export default function AdminTopbar({ isSidebarCollapsed, toggleSidebar, setCurrentView }: AdminTopbarProps) {
  const { user } = useAuth();
  const { iconUrl, appName } = useBranding();
  const [showNotificationModal, setShowNotificationModal] = useState(false);

  return (
    <header className="h-[64px] bg-white border-b border-[#EDEDF0] flex items-center justify-between px-4 md:px-6 sticky top-0 z-20">
      <div className="flex items-center gap-2 text-[12.5px] text-[#8A8D93] tracking-tight">
        <button 
          onClick={toggleSidebar}
          className="md:hidden w-8 h-8 rounded-[10px] bg-[#0E0E10] text-white grid place-items-center font-bold"
        >
          C
        </button>
            <span className="hidden sm:inline">{appName} Admin</span>
        <span className="hidden sm:inline opacity-40">/</span>
        <span className="text-[#0F0F10] font-[500]">Overview</span>
      </div>

      <div className="hidden lg:flex items-center">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
          <input 
            placeholder="Pesquisar..." 
            className="h-9 w-[320px] rounded-full bg-[#F7F8F9] border border-[#EDEDF0] pl-9 pr-12 text-[13px] placeholder:text-[#A1A1AA] outline-none focus:border-[#D1D1D6] focus:bg-white transition" 
          />
          <span className="absolute right-2 top-1/2 -translate-y-1/2 h-6 px-1.5 rounded-[6px] bg-white border border-[#E2E2E5] text-[11px] font-medium text-[#8A8D93] grid place-items-center font-mono">
            ⌘K
          </span>
        </div>
      </div>

      <div className="flex items-center gap-2.5">
        <div className="hidden xl:flex items-center gap-2">
          <div className="h-7 px-3 rounded-full border border-[#E2E2E5] bg-white flex items-center gap-1.5 text-[11px] font-[600] tracking-wide">
            <span className="w-1.5 h-1.5 rounded-full bg-[#10B981]" />
            PLATFORM STABLE
          </div>
          <div className="h-7 px-3 rounded-full bg-[#0F0F10] text-white text-[11px] font-[600] flex items-center gap-1.5">
            <span className="w-4 h-4 rounded-full bg-white text-black grid place-items-center text-[10px] font-bold overflow-hidden">
              {iconUrl ? <img src={iconUrl} alt={appName} className="w-full h-full object-contain" /> : appName.charAt(0).toUpperCase()}
            </span>
            {appName}
          </div>
        </div>

        <div className="flex items-center gap-1">
          <button className="w-8 h-8 rounded-full border border-[#EDEDF0] bg-white grid place-items-center text-[#6B7280] hover:text-[#0F0F10] transition-colors">
            <Moon size={15} />
          </button>
          <button className="w-8 h-8 rounded-full border border-[#EDEDF0] bg-white grid place-items-center text-[#6B7280] hover:text-[#0F0F10] transition-colors">
            <CircleHelp size={15} />
          </button>
          <div className="relative">
            <button 
              onClick={() => setShowNotificationModal(!showNotificationModal)}
              className="w-8 h-8 rounded-full border border-[#EDEDF0] bg-white grid place-items-center text-[#6B7280] hover:text-[#0F0F10] transition-colors"
            >
              <Bell size={15} />
              <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 bg-[#0F0F10] rounded-full" />
            </button>
            {showNotificationModal && (
              <div className="absolute right-0 mt-2 w-80 bg-white border border-[#E2E2E5] rounded-[16px] shadow-[0_1px_2px_rgba(0,0,0,0.04)] z-50 p-4 text-left">
                <div className="flex items-center justify-between pb-3 border-b border-[#EDEDF0] mb-3">
                  <span className="text-[11px] font-bold text-[#0F0F10] tracking-wide">NOTIFICAÇÕES</span>
                </div>
                <div className="space-y-3">
                  <div className="flex gap-2.5 items-start">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#10B981] mt-1.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-[12px] font-bold text-[#0F0F10]">System Update</p>
                      <p className="text-[11px] text-[#6B7280] mt-0.5">All systems operational.</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          <button onClick={() => setCurrentView('dashboard')} className="ml-1 w-8 h-8 rounded-full overflow-hidden border border-[#EDEDF0] transition-all hover:ring-2 hover:ring-[#E2E2E5]">
            <img 
              src={user?.photoURL || "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=150&auto=format&fit=crop&q=80"} 
              className="w-full h-full object-cover" 
              alt="avatar"
              referrerPolicy="no-referrer"
            />
          </button>
        </div>
      </div>
    </header>
  );
}
